import { LeanToPost } from "../../scene/extras/leanto/LeanToPost";
import { Object3D, Material, Mesh } from "three";
import { Building } from "../Building";
import { Main } from "../../Main";
import type { LeanTo } from "scene/extras/leanto/LeanTo";

export interface IPostManagerConfig
{
	material?: Material;
	noCorners?: boolean;
}

/**
 * Manages posts according to a length value.
 * Post: Aka Column. Used on the side of the building when the "no wall" option is activated
 * and for lean tos.
 */
export class PostManager
{
	public static post_width = 1.5 / 4; // height / 33;
	public static IntervalOnSide = 25;
	public static IntervalOnEnd = 20;

	private _config: IPostManagerConfig;
	private _isSide: boolean;
	private _interval: number;

	private _posts: LeanToPost[] = []
	private _insetBayPosts: LeanToPost[] = []
	private _postTransforms: Mesh[] = [];

	private _leanTo?: LeanTo;

	private _container: Object3D = new Object3D();

	constructor(isSide:boolean, config: IPostManagerConfig, leanTo?: LeanTo)
	{
		this._config = config;
		this._isSide = isSide;

		this._leanTo = leanTo;

		this._interval = this._isSide ? PostManager.IntervalOnSide : PostManager.IntervalOnEnd;
	}

	public update(length: number, height: number, whichSide?: string, zOffset?: number)
	{
		// create as many posts as needed
		// count the number of 25' bays (aka blocks) rounded up,
		// each block should have a post on both sides.

		if (whichSide === Building.WALLS.LEFT)
		{
			this._container.position.z = -zOffset;
		}
		else if (whichSide === Building.WALLS.RIGHT)
		{
			this._container.position.z = zOffset;
		}

		var numberOfBays = Math.ceil(length / this._interval);
		var requiredPosts = numberOfBays + 1;

		var postsToCreate = requiredPosts - this._posts.length;
		var material = this._config.material;

		for (var i = 0; i < postsToCreate; ++i)
		{
			var post = new LeanToPost({
				material: material
			});

			post.container.rotation.x = -Math.PI/2;
			this._posts.push(post);
			this._container.add(post.container);
		}

		// if postsToCreate is < 0 -> delete unnecessary ones
		for (i = 0; i < -postsToCreate; ++i)
		{
			var postToDelete = this._posts.pop();
			this._container.remove(postToDelete.container);
			//postToDelete.geometry.dispose();
			//postToDelete.material.dispose();
		}


		//
		// set posts for the leantos which are next to insetbays
		//

		if (this._isSide)
		{
			for (var i = this._insetBayPosts.length - 1; i >= 0; --i)
			{
				var postToDelete = this._insetBayPosts.pop();
				this._container.remove(postToDelete.container);
			}

			var insetBays = Main.getInstance().config.building.insetBay;
			for (var i = 0; i < insetBays.front.count.value; ++i)
			{
				var post = new LeanToPost({material: material});
				post.container.rotation.x = -Math.PI/2;
				post.container.scale.z = height;
				post.container.scale.x = post.container.scale.y = PostManager.post_width;

				var insetBayLength = i === 0 ? insetBays.front.length_1.value : insetBays.front.length_1.value + insetBays.front.length_2.value;
				var newZPos = whichSide === Building.WALLS.RIGHT ? -Main.getInstance().config.building.length.value / 2 - insetBayLength + PostManager.post_width : Main.getInstance().config.building.length.value / 2 + insetBayLength - PostManager.post_width;
				post.container.position.z = newZPos;
				post.container.position.y = height / 2;

				this._insetBayPosts.push(post);

				this._container.add(post.container);
			}
			for (var i = 0; i < insetBays.back.count.value; ++i)
			{
				var post = new LeanToPost({material: material});
				post.container.rotation.x = -Math.PI/2;
				post.container.scale.z = height;
				post.container.scale.x = post.container.scale.y = PostManager.post_width;

				var insetBayLength = i === 0 ? insetBays.back.length_1.value : insetBays.back.length_1.value + insetBays.back.length_2.value;
				var newZPos = whichSide === Building.WALLS.RIGHT ? Main.getInstance().config.building.length.value / 2 + insetBayLength - PostManager.post_width : -Main.getInstance().config.building.length.value / 2 - insetBayLength + PostManager.post_width;
				post.container.position.z = newZPos;
				post.container.position.y = height / 2;

				this._insetBayPosts.push(post);

				this._container.add(post.container);
			}
		}


		// update posts

		this._postTransforms.length = 0;

		for (i = 0; i < this._posts.length; ++i)
		{
			var post_container = this._posts[i].container;

			// reset visible in case noCorners changed it (see below)
			post_container.visible = true;

			// aligned by equal distance
			post_container.position.z = -length/2 + i / (this._posts.length-1) * length;

			if (this._isSide)
			{
				var zOffset = 0;
				zOffset += Main.getInstance().config.building.leanto[whichSide].cut_front.value / 2;
				zOffset -= Main.getInstance().config.building.leanto[whichSide].cut_back.value / 2;

				if (whichSide === "left")
				{
					zOffset *= -1;
				}

				post_container.position.z += zOffset;
			}

			//post_container.position.x = -width + PostManager.post_width;
			post_container.position.y = height / 2;

			// small adjustments for first and last posts to align with edge
			if (i === 0)
			{
				post_container.position.z += PostManager.post_width;
			}
			else if (i === this._posts.length-1)
			{
				post_container.position.z -= PostManager.post_width;
			}

			post_container.scale.z = height;
			post_container.scale.x = post_container.scale.y = PostManager.post_width;

			this._postTransforms[i] = post_container;
		}

		for (var i = 0; i < this._insetBayPosts.length; ++i)
		{
			var post_container = this._insetBayPosts[i].container;
			this._postTransforms.push(post_container);
		}

		if (this._config.noCorners)
		{
			// hide first and last posts

			if (this._posts[0])
			{
				this._posts[0].container.visible = false;
			}
			if (this._posts[this._posts.length-1])
			{
				this._posts[this._posts.length-1].container.visible = false;
			}
		}
	}

	public get postTransforms(): {position: number, offset: number}[]
	{
		if (this._leanTo)
		{
			if (!this._leanTo.config.enabled.value)
			{
				return [];
			}
		}

		let posOffset = 0;

		if (this._leanTo?.id === "front" || this._leanTo?.id === "back")
		{
			// For some reason, the "left" and "right" posts have these offsets within their positions...
			posOffset = this._leanTo ? ((this._leanTo.config.cut_back.value - this._leanTo.config.cut_front.value) / 2) : 0;
		}

		return this._postTransforms.map(p => ({
			position: p.position.z,
			offset: posOffset
		}));
	}

	public get container()
	{
		return this._container;
	}
}